import {
    createStyles,
    FormControl,
    FormHelperText,
    Input as MuiInput,
    InputLabel,
    makeStyles,
} from "@material-ui/core";
import { TextFieldProps } from "@material-ui/core/TextField";
import classnames from "classnames";
import * as React from "react";

import { RelodeTheme } from "../";
import { UnusedAriaProps, UnusedMuiEvents, UnusedMuiProps } from "../types";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            characterLimit: {
                fontSize: 12,
                color: theme.relode.colors.backgroundText.main,
                right: 0,
                bottom: 0,
                position: "absolute",
                marginBottom: -20,
            },
            characterLimitNear: {
                color: theme.relode.colors.backgroundText.dark,
                fontWeight: "bold",
            },
        }),
    { name: "Input" }
);

export type InputProps = {
    onEnterKey?: () => void;
    hideCharacterLimit?: boolean;
    dataCy?: string;
} & Omit<
    TextFieldProps,
    | UnusedMuiEvents
    | UnusedAriaProps
    | UnusedMuiProps
    | "select"
    | "SelectProps"
> &
    Pick<TextFieldProps, "onBlur" | "onChange" | "onFocus" | "onKeyPress">;

interface State {
    value?:
        | string
        | number
        | boolean
        | Array<string | number | boolean>
        | unknown;
}

export const Input = (props: InputProps) => {
    const {
        autoComplete,
        autoFocus,
        children,
        className,
        dataCy,
        defaultValue,
        error,
        FormHelperTextProps,
        fullWidth,
        helperText,
        id,
        InputLabelProps,
        inputProps,
        InputProps,
        inputRef,
        label,
        multiline,
        name,
        onBlur,
        onChange,
        onFocus,
        placeholder,
        required,
        rows,
        rowsMax,
        type,
        value = "",
        classes: classesProp,
        onEnterKey,
        hideCharacterLimit = false,
        ...passthrough
    } = props;

    const classes = useStyles(props);
    const [state, setState] = React.useState<State>(undefined);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !!props.onEnterKey) {
            props.onEnterKey();
        }
    };
    // null or undefined then default to empty string
    const newValue = value ?? "";
    const handleChangeValue = onChange;

    const helperTextId =
        helperText && id ? `${id}-helper-text-where` : undefined;

    const maxLength = !!inputProps ? inputProps.maxLength : undefined;
    let remaining = maxLength;

    let characterLimitMessage = "";

    if (typeof value === "string") {
        characterLimitMessage = `${value.length} / ${maxLength}`;
        remaining = maxLength - value.length;
    }

    const characterClasses = classnames({
        [classes.characterLimit]: true,
        [classes.characterLimitNear]: remaining < 10 && remaining >= 0,
    });

    return (
        <FormControl
            aria-describedby={helperTextId}
            className={className}
            error={error}
            fullWidth={fullWidth}
            required={required}
            {...passthrough}
        >
            {!!label && (
                <InputLabel htmlFor={id} {...InputLabelProps}>
                    {label}
                </InputLabel>
            )}
            <MuiInput
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                data-cy={dataCy}
                fullWidth={fullWidth}
                multiline={multiline}
                name={name}
                rows={rows}
                rowsMax={rowsMax}
                type={type}
                id={"input" + id?.charAt(0).toUpperCase() + id?.slice(1)}
                inputRef={inputRef}
                onBlur={onBlur}
                onChange={handleChangeValue}
                onFocus={onFocus}
                onKeyPress={handleKeyPress}
                placeholder={placeholder}
                inputProps={inputProps}
                value={newValue}
                {...InputProps}
            />
            {!hideCharacterLimit && !!maxLength && (
                <div className={characterClasses}>
                    character limit: {characterLimitMessage}
                </div>
            )}
            {!!helperText ? (
                <FormHelperText id={helperTextId} {...FormHelperTextProps}>
                    {helperText}
                </FormHelperText>
            ) : null}
        </FormControl>
    );
};
